// ============================================================================
// Remembered tab — the tab an operator LEFT is the tab they come back to.
//
// Every OMS list screen (Overview, Operations, PO) is a set of tabs over one table,
// and the thing almost everyone does on arrival is SEARCH for an order they already
// have in hand — an SO number off a WhatsApp message, an ENQ a colleague read out, a
// PO number a supplier is asking about. So each of those screens leads with a Search
// tab that fetches NOTHING until something is typed, and that is where anyone who has
// never picked a tab lands. Before this, every visit paid for a 50-row board (plus its
// tab counts) that the operator scrolled straight past on their way to the search box.
//
// After that it is their own last choice that wins: someone who works First Mile all
// day lands on First Mile, someone who only ever looks orders up lands on Search. That
// is deliberately the operator's own behaviour and nothing else — not the state of a
// queue, which is the pattern Operations used to have (it jumped to Pending Tasks
// whenever that inbox was non-empty, so the view you got depended on data rather than
// on what you were doing, and a deep link re-routed itself out from under you).
//
// Stored per screen in localStorage: a per-browser convenience, not state anyone else
// reads. Both ends are guarded — localStorage throws outright in a locked-down browser,
// and a stored key that no longer exists (a tab renamed or dropped in a later release)
// must fall back to the default rather than strand someone on a blank page.
// ============================================================================
import { useEffect, useState } from "react";

export const useRememberedTab = <T extends string>(
  storageKey: string,
  tabKeys: readonly T[],
  defaultKey: T
): [T, (next: T) => void] => {
  // Read once, at mount — re-reading on render would fight the operator's own clicks.
  const [tab, setTab] = useState<T>(() => {
    try {
      const saved = window.localStorage.getItem(storageKey) as T | null;
      return saved && tabKeys.includes(saved) ? saved : defaultKey;
    } catch {
      return defaultKey;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, tab);
    } catch {
      /* private mode / blocked site data — remembering is a convenience, not a requirement */
    }
  }, [storageKey, tab]);

  return [tab, setTab];
};
